/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Conversations Contract - oRPC API Contract for Conversation Operations
 *
 * Endpoints:
 * - GET /api/conversations/:phoneE164 - List conversation messages
 * - GET /api/conversations/calendar/events - List calendar events
 * - POST /api/conversations/:phoneE164 - Append message to conversation
 * - POST /api/conversations/:phoneE164/record - Record inbound message
 * - DELETE /api/conversations/:phoneE164 - Clear conversation
 */
import { oc } from "@orpc/contract";
import * as z from "zod";
import { DataAgentWorkflowPartSchema } from "./agent-workflow.contract";
import { commonErrors } from "./base";

// ============================================================================
// Schemas
// ============================================================================

/**
 * Conversation channel slug — declares which integration a conversation
 * row lives on. There is no "default channel"; every write must specify
 * it (inbound webhook adapters know their channel from the URL they're
 * mounted at; outbound replies use the channel of the conversation
 * they're replying to).
 *
 * Adding a new integration: append the slug here and wire its inbound
 * webhook adapter to record messages with the matching value.
 */
export const ConversationChannelSchema = z.enum([
	"whatsapp",
	"sms",
	"email",
	"telegram",
	"messenger",
	// `system_agent` is the operator-facing AI-chat channel — kept here so
	// the API contract recognises messages persisted by the system-agent
	// assistant flow. Not a customer-facing integration.
	"system_agent",
]);
export type ConversationChannel = z.infer<typeof ConversationChannelSchema>;

/**
 * Conversation message role enum
 */
export const ConversationRoleSchema = z.enum([
	"user",
	"assistant",
	"secretary",
	"admin",
	"tool",
	"system",
]);

/**
 * Tool invocation in AI SDK format
 */
export const ToolInvocationSchema = z.object({
	toolName: z.string().max(200),
	toolCallId: z.string().max(200),
	state: z.enum([
		"input-streaming",
		"input-available",
		"output-available",
		"output-error",
		"approval-requested",
		"approval-responded",
		"output-denied",
	]),
	input: z.unknown().nullish(),
	output: z.unknown().nullish(),
	errorText: z.string().max(1000).nullish(),
	selectedOptionId: z.string().max(256).nullish(),
	approval: z
		.object({
			id: z.string().max(200),
			approved: z.boolean().nullish(),
			reason: z.string().max(1000).nullish(),
		})
		.nullish(),
	sequence: z.number().nullish(),
});

/**
 * Tool state enum - all possible states for tool parts
 */
export const ToolStateSchema = z.enum([
	"input-streaming",
	"input-available",
	"output-available",
	"approval-requested",
	"output-denied",
	"approval-responded",
	"output-error",
]);

/**
 * Approval object schema for tool parts
 */
export const ToolApprovalSchema = z.object({
	id: z.string().max(200),
	approved: z.boolean().nullish(),
	reason: z.string().max(1000).nullish(),
});

/**
 * Text part schema
 */
export const TextPartSchema = z.object({
	type: z.literal("text"),
	text: z.string().max(100_000),
});

/**
 * Reasoning part schema
 */
export const ReasoningPartSchema = z.object({
	type: z.literal("reasoning"),
	text: z.string().max(100_000),
});

/**
 * File part schema
 */
export const FilePartSchema = z.object({
	type: z.literal("file"),
	url: z.string().min(1),
	filename: z.string().nullish(),
	mediaType: z.string().min(1),
	id: z.string().nullish(),
	size: z.number().int().positive().nullish(),
});

/**
 * Source part schema
 */
export const SourcePartSchema = z.object({
	type: z.literal("source"),
	source: z.object({
		url: z.string().url(),
		title: z.string().nullish(),
		description: z.string().nullish(),
	}),
	citationIndex: z.number().int().nonnegative().nullish(),
});

/**
 * Base tool part fields shared by static and dynamic tool parts
 */
const baseToolPartFields = {
	toolCallId: z.string(),
	state: ToolStateSchema,
	input: z.record(z.string(), z.unknown()).nullish(),
	output: z.record(z.string(), z.unknown()).nullish(),
	errorText: z.string().nullish(),
	/**
	 * Customer selection reflected onto an interactive send tool part by the
	 * conversation projection. It is not a secretary-operated control.
	 */
	selectedOptionId: z.string().max(256).nullish(),
	approval: ToolApprovalSchema.nullish(),
};

/**
 * State-conditional refinement for tool parts, mirroring the server's
 * `conversation-message.schema.ts`: AI SDK v7 keeps approval identity in the
 * nested `approval` object, in an exact shape per state.
 *   - `approval-requested`: `approval { id }`, no decision yet;
 *   - `approval-responded`: a boolean decision;
 *   - `output-denied`: `approved: false`;
 *   - `output-available` / `output-error`: no approval, or an approved one;
 *   - any other state: no approval.
 *
 * This prevents stale approval metadata from leaking into terminal states
 * like `output-error` (e.g. a tool that fails AFTER the user approved it).
 */
function refineApprovalMetadata(
	part: {
		state: z.infer<typeof ToolStateSchema>;
		approval?: z.infer<typeof ToolApprovalSchema> | null;
	},
	ctx: z.RefinementCtx
): void {
	const approval = part.approval ?? undefined;
	const issue = (message: string) =>
		ctx.addIssue({ code: "custom", message, path: ["approval"] });
	switch (part.state) {
		case "approval-requested":
			if (!approval || approval.approved != null) {
				issue("approval-requested requires approval { id } without a decision");
			}
			return;
		case "approval-responded":
			if (typeof approval?.approved !== "boolean") {
				issue("approval-responded requires an approval decision");
			}
			return;
		case "output-denied":
			if (approval?.approved !== false) {
				issue("output-denied requires approval.approved=false");
			}
			return;
		case "output-available":
		case "output-error":
			if (approval && approval.approved !== true) {
				issue(`${part.state} approval must be an approved decision`);
			}
			return;
		default:
			if (approval) {
				issue(`approval is not allowed for ${part.state}`);
			}
	}
}

/**
 * Tool part schema for static tool types (tool-search, tool-calendar, etc.)
 * The type field uses a template literal pattern matching 'tool-*'
 *
 * The `*Raw` variant skips state-vs-approval refinement and is intended for
 * parsing untrusted raw input (e.g. database payload deserialization) that
 * may carry stale `approvalId` / `approval` from a prior tool-call state.
 * The downstream mapper strips those fields before re-validating against
 * the strict (refined) schema.
 */
export const StaticToolPartSchemaRaw = z.object({
	type: z
		.string()
		.refine(
			(v): v is `tool-${string}` =>
				v.startsWith("tool-") && v !== "tool-call" && v !== "tool-result",
			{
				message:
					'Static tool part type must start with "tool-" (excluding tool-call and tool-result)',
			}
		),
	...baseToolPartFields,
});
export const StaticToolPartSchema = StaticToolPartSchemaRaw.superRefine(
	refineApprovalMetadata
);

/**
 * Dynamic tool part schema for runtime-defined tools
 * Uses type: 'dynamic-tool' with a toolName field
 */
export const DynamicToolPartSchemaRaw = z.object({
	type: z.literal("dynamic-tool"),
	toolName: z.string(),
	...baseToolPartFields,
});
export const DynamicToolPartSchema = DynamicToolPartSchemaRaw.superRefine(
	refineApprovalMetadata
);

/**
 * Raw (unrefined) message part schema for parsing untrusted raw input where
 * tool parts may carry stale approval metadata that the mapper will strip
 * before final validation.
 */
export const MessagePartSchemaRaw = z.union([
	TextPartSchema,
	ReasoningPartSchema,
	FilePartSchema,
	SourcePartSchema,
	StaticToolPartSchemaRaw,
	DynamicToolPartSchemaRaw,
	DataAgentWorkflowPartSchema,
]);

/**
 * Tool part schema - union of static and dynamic tool parts
 */
export const ToolPartSchema = z.union([
	StaticToolPartSchema,
	DynamicToolPartSchema,
]);

/**
 * Message part schema (text, reasoning, file, source, or tool parts)
 *
 * This is a union schema that supports:
 * - Text parts: { type: 'text', text: string }
 * - Reasoning parts: { type: 'reasoning', text: string }
 * - File parts: { type: 'file', url, mediaType, ... }
 * - Source parts: { type: 'source', source: { url, title, ... } }
 * - Static tool parts: { type: 'tool-*', toolCallId, state, ... }
 * - Dynamic tool parts: { type: 'dynamic-tool', toolName, toolCallId, state, ... }
 * - Agent-workflow receipt parts: { type: 'data-agent-workflow', data: {...} }
 */
export const MessagePartSchema = z.union([
	TextPartSchema,
	ReasoningPartSchema,
	FilePartSchema,
	SourcePartSchema,
	StaticToolPartSchema,
	DynamicToolPartSchema,
	DataAgentWorkflowPartSchema,
]);

/**
 * AI generation metrics persisted with an assistant message.
 *
 * Structural copy of the domain `AiUsageCostData` shape
 * (`domain/contracts/ui-message-stream.types.ts`) — the same payload the
 * backend emits as the `data-usage` stream part. Persisted in the message
 * payload so chat history can render per-response speed/diagnostics after
 * a reload, not just for the live-streamed turn.
 */
export const AiOutputChunkTimingStatsSchema = z.object({
	min: z.number().optional(),
	p10: z.number().optional(),
	median: z.number().optional(),
	avg: z.number().optional(),
	p90: z.number().optional(),
	max: z.number().optional(),
});

export const AiGenerationPerformanceSchema = z.object({
	effectiveOutputTokensPerSecond: z.number().optional(),
	outputTokensPerSecond: z.number().optional(),
	inputTokensPerSecond: z.number().optional(),
	effectiveTotalTokensPerSecond: z.number().optional(),
	stepTimeMs: z.number().optional(),
	responseTimeMs: z.number().optional(),
	toolExecutionMs: z.record(z.string(), z.number()).optional(),
	toolExecutionTotalMs: z.number().optional(),
	toolExecutionMaxMs: z.number().optional(),
	timeToFirstOutputMs: z.number().optional(),
	timeBetweenOutputChunksMs: AiOutputChunkTimingStatsSchema.optional(),
});

export const AiGenerationStepSchema = z.object({
	stepNumber: z.number().int(),
	provider: z.string().max(200).optional(),
	modelId: z.string().max(200).optional(),
	finishReason: z.string().max(200).optional(),
	rawFinishReason: z.string().max(200).optional(),
	inputTokens: z.number().int().optional(),
	outputTokens: z.number().int().optional(),
	totalTokens: z.number().int().optional(),
	toolCallCount: z.number().int().optional(),
	toolResultCount: z.number().int().optional(),
	toolErrorCount: z.number().int().optional(),
	toolNames: z.array(z.string().max(200)).max(50).optional(),
	performance: AiGenerationPerformanceSchema.optional(),
});

export const AiAudioTranscriptionMetricSchema = z.object({
	provider: z.string().max(200),
	model: z.string().max(200),
	durationSeconds: z.number().nonnegative(),
	costUsd: z.number().nonnegative(),
	latencyMs: z.number().nonnegative(),
	costSource: z.literal("estimated"),
});

export const AiMessageMetricsSchema = z.object({
	audioTranscriptions: z
		.array(AiAudioTranscriptionMetricSchema)
		.max(8)
		.optional(),
	inputTokens: z.number().int().optional(),
	outputTokens: z.number().int().optional(),
	totalTokens: z.number().int().optional(),
	cachedInputTokens: z.number().int().optional(),
	cacheWriteInputTokens: z.number().int().optional(),
	noCacheInputTokens: z.number().int().optional(),
	textOutputTokens: z.number().int().optional(),
	reasoningTokens: z.number().int().optional(),
	modelId: z.string().max(200).optional(),
	provider: z.string().max(200).optional(),
	finishReason: z.string().max(200).nullable().optional(),
	status: z.enum(["success", "error", "aborted"]).optional(),
	stepCount: z.number().int().optional(),
	toolCallCount: z.number().int().optional(),
	toolResultCount: z.number().int().optional(),
	toolErrorCount: z.number().int().optional(),
	hasUsage: z.boolean().optional(),
	hasPerformance: z.boolean().optional(),
	performance: AiGenerationPerformanceSchema.optional(),
	steps: z.array(AiGenerationStepSchema).max(50).optional(),
	costUSD: z
		.object({
			inputUSD: z.number().optional(),
			outputUSD: z.number().optional(),
			cacheReadUSD: z.number().optional(),
			reasoningUSD: z.number().optional(),
			totalUSD: z.number().optional(),
		})
		.optional(),
	context: z
		.object({
			inputMax: z.number().optional(),
			outputMax: z.number().optional(),
			totalMax: z.number().optional(),
			combinedMax: z.number().optional(),
		})
		.optional(),
	/**
	 * Draft generations cancelled because the customer kept typing before the
	 * reply finished. Their spend is folded into `costUSD.totalUSD`; itemized
	 * here so the metrics chip can explain the blended total.
	 */
	superseded: z
		.object({
			count: z.number().int().nonnegative(),
			inputTokens: z.number().int().nonnegative().optional(),
			outputTokens: z.number().int().nonnegative().optional(),
			totalTokens: z.number().int().nonnegative().optional(),
			costUsd: z.number().nonnegative().optional(),
		})
		.optional(),
});

/**
 * A single reaction stamped onto a *target* message row.
 *
 * WhatsApp replaces a sender's previous reaction, so at most ONE entry per
 * direction lives on a row: INBOUND = the WhatsApp end-user reacted; OUTBOUND
 * = our staff reacted via the web UI.
 */
export const MessageReactionSchema = z.object({
	emoji: z.string().min(1),
	direction: z.enum(["INBOUND", "OUTBOUND"]),
	sentAt: z.string(),
	externalMessageId: z.string().nullish(),
});

/**
 * Set on an INBOUND row created from a button / list / template-quick-reply tap.
 */
export const MessageButtonReplySchema = z.object({
	id: z.string(),
	title: z.string(),
	kind: z.enum(["interactive_button", "interactive_list", "template_button"]),
	contextExternalMessageId: z.string().nullish(),
	inReplyToMessageId: z.string().nullish(),
	description: z.string().nullish(),
});

/**
 * Set on an OUTBOUND row persisted for an AI-tool-sent interactive-button
 * message: the body text plus the 1-3 reply buttons that were offered.
 */
export const MessageInteractiveButtonsSchema = z.object({
	bodyText: z.string(),
	buttons: z
		.array(z.object({ id: z.string(), title: z.string() }))
		.min(1)
		.max(3),
	headerText: z.string().nullish(),
	footerText: z.string().nullish(),
});

/**
 * Set on a row that quotes an earlier message (a WhatsApp-style reply). Mirror
 * of the domain ReplyToSchema. `messageId` is our DB Message.id (decimal string)
 * of the quoted row when resolved; `externalMessageId` is the quoted message's
 * wamid; `preview` is a single-line ≤140-char excerpt; `role` echoes the quoted
 * sender. At least one of messageId/externalMessageId is set.
 */
export const ReplyToSchema = z.object({
	messageId: z.string().nullish(),
	externalMessageId: z.string().nullish(),
	preview: z.string().max(140),
	role: z.string().nullish(),
});

/**
 * Conversation message schema
 */
export const ReminderSnapshotSchema = z.object({
	templateName: z.string().min(1).nullable(),
	language: z.string().min(1).nullable(),
	label: z.string().min(1).nullable(),
	content: z.string().nullable(),
});
export type ReminderSnapshot = z.infer<typeof ReminderSnapshotSchema>;

export const ConversationMessageSchema = z.object({
	id: z.string().max(200).nullish(),
	role: ConversationRoleSchema,
	message: z.string().max(100_000),
	sentAt: z.string().datetime(),
	parts: z.array(MessagePartSchema).max(100).nullish(),
	tool_call: z
		.object({
			tool_name: z.string().max(200),
			tool_call_id: z.string().max(200),
			tool_args: z.record(z.string(), z.unknown()).nullish(),
			tool_result: z.record(z.string(), z.unknown()).nullish(),
			tool_error: z.string().or(z.record(z.string(), z.unknown())).nullish(),
		})
		.nullish(),
	toolInvocations: z.array(ToolInvocationSchema).max(50).nullish(),
	reasoning: z.string().max(100_000).nullish(),
	sources: z
		.array(
			z.object({
				url: z.string().url().max(2048),
				title: z.string().max(500).nullish(),
				description: z.string().max(2000).nullish(),
			})
		)
		.max(20)
		.nullish(),
	conversationId: z.string().max(200).nullish(),
	externalMessageId: z.string().max(255).nullish(),
	/**
	 * AI generation diagnostics for assistant turns (speed, tokens, cost).
	 * Absent on user turns and on assistant turns persisted before this
	 * field existed.
	 */
	aiMetrics: AiMessageMetricsSchema.nullish(),
	/**
	 * Reactions stamped on this row (at most one per direction). Surfaced so
	 * the web UI can render a reaction pill on the target message.
	 */
	reactions: z.array(MessageReactionSchema).max(2).nullish(),
	/** Present on an INBOUND turn created from a button/list/template-reply tap. */
	buttonReply: MessageButtonReplySchema.nullish(),
	/** Present on an OUTBOUND AI-tool-sent interactive-button turn. */
	interactiveButtons: MessageInteractiveButtonsSchema.nullish(),
	reminder: ReminderSnapshotSchema.nullish(),
	/**
	 * Present on a row that quotes an earlier message: an operator reply
	 * (OUTBOUND) or an end-user quoted reply (INBOUND).
	 */
	replyTo: ReplyToSchema.nullish(),
	/**
	 * OUTBOUND delivery lifecycle, from WhatsApp delivery receipts. The read
	 * mapper lowercases the row's Message.sendStatus scalar; absent/null on
	 * inbound turns and rows with no send lifecycle. Never persisted into the
	 * payload JSON — it always reflects the scalar column.
	 */
	deliveryStatus: z
		.enum(["pending", "sending", "sent", "delivered", "read", "failed"])
		.nullish(),
});

/**
 * Calendar event schema for conversation events display
 */
export const ConversationCalendarEventSchema = z.object({
	id: z.string(),
	title: z.string(),
	start: z.string().datetime(),
	end: z.string().datetime().nullish(),
	allDay: z.boolean().nullish(),
	extendedProps: z.record(z.string(), z.unknown()).nullish(),
	user_name: z.string().nullish(),
});

/**
 * Messages map: Record<phoneE164, ConversationMessage[]>
 */
export const ConversationMessagesMapSchema = z.record(
	z.string(),
	z.array(ConversationMessageSchema)
);

/**
 * Calendar events map: Record<date, ConversationCalendarEvent[]>
 */
export const ConversationCalendarEventsMapSchema = z.record(
	z.string(),
	z.array(ConversationCalendarEventSchema)
);

// ============================================================================
// Error Definitions
// ============================================================================

export const conversationErrors = {
	INVALID_PHONE: {
		message: "Invalid phone number format",
		data: z.object({
			phoneE164: z.string(),
		}),
	},
	USER_NOT_FOUND: {
		message: "User not found",
		data: z.object({
			phoneE164: z.string(),
		}),
	},
	USER_LOOKUP_FAILED: {
		message: "Failed to get or create user",
		data: z.object({
			phoneE164: z.string(),
		}),
	},
	MISSING_FIELDS: {
		message: "Missing required fields: role and message",
	},
	INVALID_MESSAGE_PAYLOAD: {
		message: "Invalid message payload",
		data: z.object({
			errors: z.array(
				z.object({
					path: z.string(),
					message: z.string(),
				})
			),
		}),
	},
	DATABASE_ERROR: {
		message: "Database operation failed",
		data: z
			.object({
				operation: z.string(),
			})
			.optional(),
	},
	...commonErrors,
};

// ============================================================================
// Contract Definition
// ============================================================================

export const conversationsContract = {
	/**
	 * List conversation messages for a user
	 * GET /api/conversations/{phoneE164}
	 */
	listMessages: oc
		.route({
			method: "GET",
			path: "/api/conversations/{phoneE164}",
			summary: "List conversation messages",
			description:
				"Get conversation messages for a user by phone number (E.164) with optional pagination.",
			tags: ["Conversations"],
		})
		.input(
			z.object({
				// Path param - phone number in E.164 format
				phoneE164: z
					.string()
					.regex(/^\+[1-9]\d{1,14}$/, "Invalid E164 phone format"),
				// Query params - cursor-based pagination
				limit: z.coerce.number().int().min(1).max(100).optional().default(50),
				startingAfter: z.string().max(255).optional(),
				endingBefore: z.string().max(255).optional(),
			})
		)
		.output(
			z.object({
				messages: z.array(ConversationMessageSchema),
				totalCount: z.number().int().min(0),
				hasMore: z.boolean(),
				nextCursor: z.string().nullish(),
			})
		)
		.errors(conversationErrors),

	// NOTE: `GET /conversations/calendar/events` is served by the calendar
	// contract/controller (calendar:view + standard-plan gate). The duplicate
	// entry that lived here typed the SDK against a `{ events: map }` shape the
	// live route never returns, so it has been removed.

	/**
	 * Append message to conversation
	 * POST /api/conversations/{phoneE164}
	 */
	appendMessage: oc
		.route({
			method: "POST",
			path: "/api/conversations/{phoneE164}",
			summary: "Append message to conversation",
			description: "Add a new message to a user conversation history.",
			tags: ["Conversations"],
		})
		.input(
			z.object({
				// Path param
				phoneE164: z
					.string()
					.regex(/^\+[1-9]\d{1,14}$/, "Invalid E164 phone format"),
				// Channel of the target conversation. Caller must specify; there is
				// no implicit default. The frontend passes the channel of the
				// conversation it's appending to.
				channel: ConversationChannelSchema,
				// Body params
				role: z.string().min(1).max(20),
				message: z.string().trim().min(1).max(100_000),
				sentAt: z.string().datetime().optional(),
				// Optional extended fields
				tool_call: z
					.object({
						tool_name: z.string().max(200),
						tool_call_id: z.string().max(200).optional(),
						tool_args: z.record(z.string(), z.unknown()).optional(),
						tool_result: z.record(z.string(), z.unknown()).optional(),
						tool_error: z
							.string()
							.or(z.record(z.string(), z.unknown()))
							.optional(),
					})
					.optional(),
				reasoning: z.string().max(100_000).optional(),
				parts: z.array(MessagePartSchema).max(100).optional(),
				toolInvocations: z.array(ToolInvocationSchema).max(50).optional(),
			})
		)
		.output(
			z.object({
				success: z.literal(true),
			})
		)
		.errors(conversationErrors),

	/**
	 * Clear conversation
	 * DELETE /api/conversations/{phoneE164}
	 */
	clearConversation: oc
		.route({
			method: "DELETE",
			path: "/api/conversations/{phoneE164}",
			summary: "Clear conversation",
			description: "Delete all messages for a user conversation.",
			tags: ["Conversations"],
		})
		.input(
			z.object({
				// Path param
				phoneE164: z
					.string()
					.regex(/^\+[1-9]\d{1,14}$/, "Invalid E164 phone format"),
			})
		)
		.output(
			z.object({
				deleted: z.number().int().min(0),
			})
		)
		.errors(conversationErrors),

	/**
	 * Get AI enabled status for a conversation
	 * GET /api/conversations/{phoneE164}/ai
	 */
	getAiEnabled: oc
		.route({
			method: "GET",
			path: "/api/conversations/{phoneE164}/ai",
			summary: "Get AI enabled status",
			description: "Check if AI auto-reply is enabled for a conversation.",
			tags: ["Conversations"],
		})
		.input(
			z.object({
				phoneE164: z
					.string()
					.regex(/^\+[1-9]\d{1,14}$/, "Invalid E164 phone format"),
			})
		)
		.output(
			z.object({
				aiEnabled: z.boolean(),
			})
		)
		.errors(conversationErrors),

	/**
	 * Toggle AI auto-reply for a conversation
	 * POST /api/conversations/{phoneE164}/ai
	 */
	toggleAi: oc
		.route({
			method: "POST",
			path: "/api/conversations/{phoneE164}/ai",
			summary: "Toggle AI auto-reply",
			description: "Enable or disable AI auto-reply for a conversation.",
			tags: ["Conversations"],
		})
		.input(
			z.object({
				phoneE164: z
					.string()
					.regex(/^\+[1-9]\d{1,14}$/, "Invalid E164 phone format"),
				enabled: z.boolean(),
			})
		)
		.output(
			z.object({
				success: z.literal(true),
				aiEnabled: z.boolean(),
			})
		)
		.errors(conversationErrors),

	/**
	 * Record inbound message
	 * POST /api/conversations/{phoneE164}/record
	 */
	recordMessage: oc
		.route({
			method: "POST",
			path: "/api/conversations/{phoneE164}/record",
			summary: "Record inbound message",
			description:
				"Record an inbound message from WhatsApp into the conversation history.",
			tags: ["Conversations"],
		})
		.input(
			z.object({
				// Path param
				phoneE164: z
					.string()
					.regex(/^\+[1-9]\d{1,14}$/, "Invalid E164 phone format"),
				// Channel of the inbound message — required. The webhook adapter
				// calling this endpoint passes the channel it's wired to.
				channel: ConversationChannelSchema,
				// Body params
				role: ConversationRoleSchema,
				message: z.string().trim().min(1).max(100_000),
				sentAt: z.string().datetime().optional(),
				externalMessageId: z.string().max(255).optional(),
				parts: z.array(MessagePartSchema).max(100).optional(),
				toolInvocations: z.array(ToolInvocationSchema).max(50).optional(),
			})
		)
		.output(
			z.object({
				success: z.literal(true),
				messageId: z.string().max(200),
			})
		)
		.errors(conversationErrors),

	/**
	 * Send (or remove) an operator reaction on a message
	 * POST /api/conversations/{phoneE164}/reaction
	 *
	 * Sends the reaction over WhatsApp then persists + broadcasts it as an
	 * OUTBOUND reaction on the target row. An empty `emoji` removes the
	 * operator's reaction (WhatsApp un-react semantics).
	 */
	sendReaction: oc
		.route({
			method: "POST",
			path: "/api/conversations/{phoneE164}/reaction",
			summary: "Send message reaction",
			description:
				"Send or remove an operator (OUTBOUND) reaction on a message in a conversation.",
			tags: ["Conversations"],
		})
		.input(
			z.object({
				// Path param
				phoneE164: z
					.string()
					.regex(/^\+[1-9]\d{1,14}$/, "Invalid E164 phone format"),
				// Body params: DB Message.id (numeric string) or the message's wamid.
				messageId: z.string().min(1).max(255),
				// Empty string removes the operator's reaction.
				emoji: z.string().max(64),
			})
		)
		.output(
			z.object({
				success: z.literal(true),
			})
		)
		.errors(conversationErrors),
};

// ============================================================================
// Type Exports
// ============================================================================

import type {
	InferContractRouterInputs,
	InferContractRouterOutputs,
} from "@orpc/contract";

export type ConversationsContract = typeof conversationsContract;

export type ConversationRole = z.infer<typeof ConversationRoleSchema>;
export type ToolInvocation = z.infer<typeof ToolInvocationSchema>;
export type ToolState = z.infer<typeof ToolStateSchema>;
export type ToolApproval = z.infer<typeof ToolApprovalSchema>;
export type TextPart = z.infer<typeof TextPartSchema>;
export type ReasoningPart = z.infer<typeof ReasoningPartSchema>;
export type FilePart = z.infer<typeof FilePartSchema>;
export type SourcePart = z.infer<typeof SourcePartSchema>;
export type StaticToolPart = z.infer<typeof StaticToolPartSchema>;
export type DynamicToolPart = z.infer<typeof DynamicToolPartSchema>;
export type ToolPart = z.infer<typeof ToolPartSchema>;
export type MessagePart = z.infer<typeof MessagePartSchema>;
export type AiMessageMetrics = z.infer<typeof AiMessageMetricsSchema>;
export type MessageReaction = z.infer<typeof MessageReactionSchema>;
export type MessageButtonReply = z.infer<typeof MessageButtonReplySchema>;
export type MessageInteractiveButtons = z.infer<
	typeof MessageInteractiveButtonsSchema
>;
export type ReplyTo = z.infer<typeof ReplyToSchema>;
export type ConversationMessage = z.infer<typeof ConversationMessageSchema>;
export type ConversationCalendarEvent = z.infer<
	typeof ConversationCalendarEventSchema
>;
export type ConversationMessagesMap = z.infer<
	typeof ConversationMessagesMapSchema
>;
export type ConversationCalendarEventsMap = z.infer<
	typeof ConversationCalendarEventsMapSchema
>;

// Contract input/output types using oRPC inference utilities
type ConversationsInputs = InferContractRouterInputs<ConversationsContract>;
type ConversationsOutputs = InferContractRouterOutputs<ConversationsContract>;

// Input/Output types for each endpoint
export type ListMessagesInput = ConversationsInputs["listMessages"];
export type ListMessagesOutput = ConversationsOutputs["listMessages"];

export type AppendMessageInput = ConversationsInputs["appendMessage"];
export type AppendMessageOutput = ConversationsOutputs["appendMessage"];

export type ClearConversationInput = ConversationsInputs["clearConversation"];
export type ClearConversationOutput = ConversationsOutputs["clearConversation"];

export type GetAiEnabledInput = ConversationsInputs["getAiEnabled"];
export type GetAiEnabledOutput = ConversationsOutputs["getAiEnabled"];

export type ToggleAiInput = ConversationsInputs["toggleAi"];
export type ToggleAiOutput = ConversationsOutputs["toggleAi"];

export type RecordMessageInput = ConversationsInputs["recordMessage"];
export type RecordMessageOutput = ConversationsOutputs["recordMessage"];

export type SendReactionInput = ConversationsInputs["sendReaction"];
export type SendReactionOutput = ConversationsOutputs["sendReaction"];
